import { useEffect, useState, useRef } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, UploadSimple, X, FolderSimple, Check, Eye, EyeSlash } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hiddenCats, setHiddenCats] = useState([]);
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ category: "", name: "", description: "", image_path: "", image_file_id: "", active: true });
  const [uploading, setUploading] = useState(false);
  const [showCatManager, setShowCatManager] = useState(false);
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [reassignTargets, setReassignTargets] = useState({});
  const fileRef = useRef();
  const editorRef = useRef();

  const load = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/products/categories").then((r) => setCategories(r.data));
    api.get("/admin/hidden-categories").then((r) => setHiddenCats(r.data.hidden || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  // Scroll the editor into view whenever it opens (fixes "clicking Edit does
  // nothing" on any product below the fold).
  useEffect(() => {
    if (editing && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [editing]);

  const startNew = () => { setEditing("new"); setForm({ category: "", name: "", description: "", image_path: "", image_file_id: "", active: true }); };
  const startEdit = (p) => {
    setEditing(p.id);
    setForm({
      category: p.category, name: p.name,
      description: p.description || "",
      image_path: p.image_path || "",
      image_file_id: p.image_file_id || "",
      active: p.active !== false,
    });
  };
  const cancel = () => { setEditing(null); };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, image_file_id: data.id, image_path: "" });
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.category.trim() || !form.name.trim()) return toast.error("Category and name required");
    try {
      if (editing === "new") await api.post("/products", form);
      else await api.patch(`/products/${editing}`, form);
      toast.success("Saved");
      setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const del = async (id) => {
    if (!confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted"); load();
  };
  const toggleProductActive = async (p) => {
    const next = !(p.active !== false);
    try {
      await api.patch(`/products/${p.id}/active`, { active: next });
      toast.success(next ? "Product visible to clients" : "Product hidden from clients");
      load();
    } catch { toast.error("Toggle failed"); }
  };
  const toggleCategoryHidden = async (cat) => {
    const currentlyHidden = hiddenCats.includes(cat);
    try {
      const { data } = await api.post("/admin/hidden-categories/toggle", { category: cat, hidden: !currentlyHidden });
      setHiddenCats(data.hidden || []);
      toast.success(!currentlyHidden ? `"${cat}" hidden from clients` : `"${cat}" visible to clients`);
    } catch { toast.error("Toggle failed"); }
  };

  const renameCat = async (oldName) => {
    if (!renameValue.trim() || renameValue === oldName) { setRenaming(null); return; }
    try {
      const { data } = await api.post("/categories/rename", { old_name: oldName, new_name: renameValue.trim() });
      toast.success(`Renamed — ${data.updated} product(s) updated`);
      setRenaming(null); setRenameValue("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rename failed");
    }
  };

  const deleteCat = async (name) => {
    const reassign = reassignTargets[name] || "";
    const productCount = products.filter(p => p.category === name).length;
    if (productCount > 0 && !reassign) return toast.error("Choose a category to reassign products to, or delete/move them first.");
    if (!confirm(`Delete category "${name}"?${reassign ? ` ${productCount} products will move to "${reassign}".` : ""}`)) return;
    try {
      const qs = reassign ? `?name=${encodeURIComponent(name)}&reassign_to=${encodeURIComponent(reassign)}` : `?name=${encodeURIComponent(name)}`;
      await api.delete(`/categories${qs}`);
      toast.success("Category deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const filtered = filter ? products.filter(p => p.category === filter) : products;

  const imageUrl = (p) => p.image_file_id ? `${API}/files/${p.image_file_id}/download` : (p.image_path || "");
  const formImageUrl = form.image_file_id ? `${API}/files/${form.image_file_id}/download` : form.image_path;

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="chip mb-3">// admin · product catalog</div>
            <h1 className="serif text-5xl md:text-6xl">Products</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowCatManager(!showCatManager)} className="btn-secondary inline-flex items-center gap-2 px-4 py-3" data-testid="manage-categories-btn">
              <FolderSimple size={18} weight="bold"/> {showCatManager ? "Close Categories" : "Manage Categories"}
            </button>
            <button onClick={startNew} className="btn-primary inline-flex items-center gap-2 px-4 py-3" data-testid="add-product-btn">
              <Plus size={18} weight="bold"/> New Product
            </button>
          </div>
        </div>

        {showCatManager && (
          <div className="border border-[var(--gc-navy)] p-6 mb-6 bg-white" data-testid="category-manager">
            <div className="chip chip-navy mb-4">// categories</div>
            <div className="space-y-2">
              {categories.map((c) => {
                const count = products.filter(p => p.category === c).length;
                const isRenaming = renaming === c;
                return (
                  <div key={c} className="flex items-center gap-3 py-2 border-b border-[var(--gc-border)]" data-testid={`cat-row-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                    {isRenaming ? (
                      <>
                        <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} className="gc-input flex-1" autoFocus data-testid={`cat-rename-input-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`} />
                        <button onClick={() => renameCat(c)} className="btn-primary px-3 py-2 text-xs" data-testid={`cat-rename-save-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}><Check size={14} weight="bold"/></button>
                        <button onClick={() => { setRenaming(null); setRenameValue(""); }} className="btn-secondary px-3 py-2 text-xs"><X size={14} weight="bold"/></button>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 flex items-center gap-3">
                          <div className="serif text-xl">{c}</div>
                          <span className="tech-label">{count} product{count === 1 ? "" : "s"}</span>
                        </div>
                        {count > 0 && (
                          <select value={reassignTargets[c] || ""} onChange={(e) => setReassignTargets({ ...reassignTargets, [c]: e.target.value })} className="gc-input text-xs max-w-[180px]" data-testid={`cat-reassign-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                            <option value="">Reassign to…</option>
                            {categories.filter(x => x !== c).map(x => <option key={x} value={x}>{x}</option>)}
                          </select>
                        )}
                        <button
                          onClick={() => toggleCategoryHidden(c)}
                          className={`text-xs px-2 py-1.5 border font-semibold inline-flex items-center gap-1 ${hiddenCats.includes(c) ? 'border-[var(--gc-orange)] bg-[var(--gc-orange)] text-white' : 'border-[var(--gc-navy)] bg-white hover:bg-[var(--gc-bg-alt)]'}`}
                          data-testid={`cat-toggle-visible-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}
                          title={hiddenCats.includes(c) ? "Hidden from clients — click to show" : "Visible to clients — click to hide"}
                        >
                          {hiddenCats.includes(c) ? <><EyeSlash size={12} weight="bold"/> Hidden</> : <><Eye size={12} weight="bold"/> Visible</>}
                        </button>
                        <button onClick={() => { setRenaming(c); setRenameValue(c); }} className="text-xs px-2 py-1.5 border border-[var(--gc-navy)] bg-white hover:bg-[var(--gc-bg-alt)] font-semibold inline-flex items-center gap-1" data-testid={`cat-edit-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                          <PencilSimple size={12} weight="bold"/> Rename
                        </button>
                        <button onClick={() => deleteCat(c)} className="text-xs px-2 py-1.5 border border-[var(--gc-danger)] text-[var(--gc-danger)] hover:bg-red-50 font-semibold inline-flex items-center gap-1" data-testid={`cat-delete-${c.replace(/[^a-z0-9]/gi, '-').toLowerCase()}`}>
                          <Trash size={12} weight="bold"/> Delete
                        </button>
                      </>
                    )}
                  </div>
                );
              })}
              <div className="text-xs text-[var(--gc-text-muted)] mt-3">
                Categories are derived from products. Rename to update all products at once. Deleting a category with products requires reassigning them to another category first. Hidden categories (and every product in them) are invisible to clients but stay visible here.
              </div>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-6">
          <button onClick={() => setFilter("")} className={`px-3 py-1.5 text-xs font-semibold border border-[var(--gc-navy)] ${!filter ? 'bg-[var(--gc-navy)] text-white' : 'bg-white hover:bg-[var(--gc-bg-alt)]'}`} data-testid="prod-filter-all">All ({products.length})</button>
          {categories.map((c) => (
            <button key={c} onClick={() => setFilter(c)} className={`px-3 py-1.5 text-xs font-semibold border border-[var(--gc-navy)] ${filter === c ? 'bg-[var(--gc-navy)] text-white' : 'bg-white hover:bg-[var(--gc-bg-alt)]'}`}>{c}</button>
          ))}
        </div>

        {editing && (
          <div ref={editorRef} className="border border-[var(--gc-orange)] p-6 mb-6 space-y-4 bg-white" data-testid="product-editor">
            <div className="chip chip-orange">{editing === "new" ? "// new product" : "// edit product"}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <div className="tech-label mb-2">Category</div>
                <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} list="cat-options" className="gc-input" data-testid="product-category" />
                <datalist id="cat-options">
                  {categories.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div>
                <div className="tech-label mb-2">Name</div>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="gc-input" data-testid="product-name" />
              </div>
            </div>
            <div>
              <div className="tech-label mb-2">Description</div>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="gc-input" data-testid="product-description" />
            </div>
            <div>
              <div className="tech-label mb-2">Product Image</div>
              <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-4 items-start">
                <div className="border border-[var(--gc-border)] bg-[var(--gc-bg-alt)] aspect-square flex items-center justify-center overflow-hidden">
                  {formImageUrl ? (
                    <img src={formImageUrl} alt="preview" className="w-full h-full object-cover" data-testid="product-image-preview" />
                  ) : (
                    <div className="text-[var(--gc-text-muted)] text-sm text-center px-2">No image</div>
                  )}
                </div>
                <div className="space-y-2">
                  <input ref={fileRef} type="file" accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0])} className="hidden" data-testid="product-image-file" />
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50" data-testid="product-image-upload-btn">
                    <UploadSimple size={16} weight="bold"/> {uploading ? "Uploading…" : (form.image_file_id ? "Replace Image" : "Upload Image")}
                  </button>
                  {form.image_file_id && (
                    <button type="button" onClick={() => setForm({ ...form, image_file_id: "", image_path: "" })} className="btn-secondary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="product-image-clear-btn">
                      <X size={14} weight="bold"/> Remove
                    </button>
                  )}
                  <div className="text-xs text-[var(--gc-text-muted)]">JPG, PNG or WebP. Stored inside the portal (MongoDB).</div>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={save} className="btn-primary px-4 py-2 text-sm" data-testid="save-product-btn">Save</button>
              <button onClick={cancel} className="btn-secondary px-4 py-2 text-sm">Cancel</button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => {
            const inactive = p.active === false;
            const catHidden = hiddenCats.includes(p.category);
            const dim = inactive || catHidden;
            return (
              <div key={p.id} className={`pb-6 border-b border-[var(--gc-border)] ${dim ? 'opacity-60' : ''}`} data-testid={`prod-card-${p.id}`}>
                {imageUrl(p) && <img src={imageUrl(p)} alt={p.name} className={`w-full h-40 object-cover border border-[var(--gc-border)] ${dim ? 'grayscale' : ''}`} />}
                <div className="tech-label mt-3 flex items-center gap-2">
                  <span>{p.category}</span>
                  {inactive && <span className="chip chip-orange text-[10px] py-0.5 px-2">// hidden</span>}
                  {catHidden && !inactive && <span className="chip chip-orange text-[10px] py-0.5 px-2">// category hidden</span>}
                </div>
                <div className="serif text-xl mt-1">{p.name}</div>
                {p.description && <div className="text-sm text-[var(--gc-text-muted)] mt-2">{p.description}</div>}
                <div className="flex gap-2 mt-3 flex-wrap">
                  <button
                    onClick={() => toggleProductActive(p)}
                    className={`text-xs px-2 py-1 border font-semibold inline-flex items-center gap-1 ${inactive ? 'border-[var(--gc-orange)] bg-[var(--gc-orange)] text-white' : 'border-[var(--gc-navy)] bg-white hover:bg-[var(--gc-bg-alt)]'}`}
                    data-testid={`prod-toggle-visible-${p.id}`}
                    title={inactive ? "Hidden — click to show to clients" : "Visible — click to hide from clients"}
                  >
                    {inactive ? <><EyeSlash size={12} weight="bold"/> Hidden</> : <><Eye size={12} weight="bold"/> Visible</>}
                  </button>
                  <button onClick={() => startEdit(p)} className="text-xs px-2 py-1 border border-[var(--gc-navy)] bg-white hover:bg-[var(--gc-bg-alt)] font-semibold inline-flex items-center gap-1"><PencilSimple size={12} weight="bold"/> Edit</button>
                  <button onClick={() => del(p.id)} className="text-xs px-2 py-1 border border-[var(--gc-danger)] text-[var(--gc-danger)] hover:bg-red-50 font-semibold inline-flex items-center gap-1"><Trash size={12} weight="bold"/> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Layout>
  );
}
